import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addDays, businessDate, compareBusinessDates, systemClock } from '@sakhtar/calendar';
import {
  V2Card,
  V2CardHeader,
  V2Table,
  V2Badge,
  V2Button,
  V2EmptyState,
  type V2TableColumn,
  type V2BadgeTone,
  LoadingState,
  NoticeBanner,
  StatePanel,
} from '@sakhtar/design-system/react';
import { AgingBucketsChart, ChartCard, formatChartAmount } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  JOURNAL_STATUS_LABEL,
  type AccountListResponse,
  type AgingResponse,
  type FinanceOverviewResponse,
  type JournalEntry,
  type JournalListResponse,
  type OpenItem,
  type OpenItemListResponse,
  type TreasuryAccountListResponse,
  type TrialBalanceResponse,
} from '../../lib/types/finance.js';
import {
  agingBucketLabel,
  amountColumnHeader,
  amountUnitLabel,
  formatRialFa,
  formatRialFaWithUnit,
  journalHref,
  type AmountDisplayUnit,
  workingOpenBalanceRial,
  workingOpenItemCount,
} from '../../lib/finance-display.js';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { compactAmountParts, percentOfRial } from './OverviewCompact.js';
import { OverviewLifecycleCard, type JournalLifecycleCounts } from './OverviewLifecycleCard.js';
import { OverviewQuickReports } from './OverviewQuickReports.js';
import { HubYarCard } from '../../shell/yar/HubYarCard.js';

const JOURNAL_STATUS_TONE: Record<string, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'info',
  posted: 'success',
  tombstoned: 'neutral',
};

/** Rows shown in the overview journals card (the frame draws five). */
const RECENT_JOURNAL_ROWS = 6;

interface JournalRow extends JournalEntry {
  readonly id: string;
}

function journalColumns(
  base: string,
  unit: AmountDisplayUnit,
): readonly V2TableColumn<JournalRow>[] {
  return [
    {
      key: 'number',
      header: 'شماره سند',
      render: (row) => (
        <Link className="ltrcell ltr sk-num" to={journalHref(base, row.journalId)}>
          {row.officialDocumentNumber ?? row.provisionalNumber}
        </Link>
      ),
    },
    {
      key: 'date',
      header: 'تاریخ',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>,
    },
    {
      key: 'description',
      header: 'شرح',
      render: (row) => row.description?.trim() || '—',
    },
    {
      key: 'debit',
      header: amountColumnHeader('بدهکار', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.debitTotal, unit)}</span>,
    },
    {
      key: 'credit',
      header: amountColumnHeader('بستانکار', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.creditTotal, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={JOURNAL_STATUS_TONE[row.status] ?? 'neutral'}>
          {JOURNAL_STATUS_LABEL[row.status] ?? 'وضعیت ثبت‌شده'}
        </V2Badge>
      ),
    },
  ];
}

/** Newest business date first; a malformed date sorts as equal rather than throwing. */
function byBusinessDateDesc(a: JournalEntry, b: JournalEntry): number {
  try {
    return compareBusinessDates(businessDate(b.businessDate), businessDate(a.businessDate));
  } catch {
    return 0;
  }
}

/** Working (open/partial) payables due from `today` through the next 7 days — calendar-package comparison only. */
function countDueThisWeek(items: readonly OpenItem[], today: ReturnType<typeof systemClock.today>) {
  const horizon = addDays(today, 7);
  return items.filter((item) => {
    if (item.status !== 'open' && item.status !== 'partial') return false;
    try {
      const due = businessDate(item.dueDate.slice(0, 10));
      return compareBusinessDates(due, today) >= 0 && compareBusinessDates(due, horizon) <= 0;
    } catch {
      return false;
    }
  }).length;
}

/**
 * Net debit balance (BigInt) of the GL accounts behind every treasury
 * account, from the posted trial balance. `null` when a treasury account's
 * GL account cannot be resolved to a chart code — the tile then shows "—"
 * instead of an understated figure. `weights` are the positive per-GL-account
 * balances scaled (BigInt) to 0..1000 for the tile's relative-size strip; they
 * are presentation proportions only, never an amount.
 */
function treasuryBalance(
  treasuryAccounts: TreasuryAccountListResponse,
  chart: AccountListResponse,
  trialBalance: TrialBalanceResponse,
): { readonly total: string; readonly weights: readonly number[] } | null {
  const codeById = new Map(chart.items.map((account) => [account.accountId, account.code]));
  const rowByCode = new Map(trialBalance.rows.map((row) => [row.accountRef, row]));
  let total = 0n;
  const perAccount: bigint[] = [];
  for (const glAccountId of new Set(treasuryAccounts.items.map((a) => a.glAccountId))) {
    const code = codeById.get(glAccountId);
    if (code === undefined) return null;
    const row = rowByCode.get(code);
    const balance = row ? BigInt(row.debitBalanceRial) - BigInt(row.creditBalanceRial) : 0n;
    total += balance;
    if (balance > 0n) perAccount.push(balance);
  }
  perAccount.sort((x, y) => (x < y ? 1 : x > y ? -1 : 0));
  const max = perAccount[0];
  const weights =
    max === undefined
      ? []
      : perAccount.map((balance) => Math.max(Number((balance * 1000n) / max), 1));
  return { total: total.toString(), weights };
}

function RetryButton({ onClick }: { readonly onClick: () => void }) {
  return (
    <V2Button variant="secondary" size="sm" onClick={onClick}>
      تلاش دوباره
    </V2Button>
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/finance`, overview
 *   tab (`finOverview`): four KPI tiles, «اسناد حسابداری» card, «چرخهٔ وضعیت
 *   سند» card, «گزارش‌های آماده» card
 * canonical pattern: MODULE_HUB (HubKpiGrid hero + 3 tiles, tinted section
 *   cards, V2Table)
 * desktop reference: finOverview (KPI row → journals card → lifecycle +
 *   quick-reports row)
 * mobile reference: v2 flex-wrap rows collapse to one column (<900px shell
 *   untouched)
 * light/dark reference: tokens only (module tint via [data-module])
 * state reference: loading · empty · permission_denied (tile/card hidden) ·
 *   network_error (retry)
 * v2 status: aligned — KPI row uses the shared HubKpi (hero + icon chips, scale,
 *   foot / footEnd, overdue progress bar, due-invoice ticks, treasury strip);
 *   cards, table, lifecycle and quick-reports follow the finOverview frame.
 * status: PARTIAL_REAL — every figure is real; nothing is hard-coded:
 *   - «حساب‌های دریافتنی» (hero): working open balance of
 *     GET .../finance/ar/open-items (BigInt sum, workingOpenBalanceRial), the
 *     overdue chip/percent from GET .../finance/ar/aging (buckets other than
 *     `current`, BigInt; percent = overdue ÷ all buckets).
 *   - «حساب‌های پرداختنی»: open balance of .../ap/open-items; «N فاکتور
 *     سررسید این هفته» counts working items whose dueDate is within
 *     [today, today+7] via @sakhtar/calendar; one tick per due invoice (max
 *     12), in the warn tone as in the frame.
 *   - «موجودی خزانه»: there is no treasury-balance endpoint, so it is derived
 *     from real ledgers: .../finance/treasury/accounts (glAccountId, kind) →
 *     .../finance/accounts (accountId → code) → posted .../finance/reports/
 *     trial-balance?asOfDate=today (debit − credit balance, BigInt). If any of
 *     the three is unreadable the tile is hidden (403) or shows "—" with
 *     retry; an unresolved GL account also shows "—". Counts «N حساب بانکی ·
 *     M صندوق» are the real `kind` values. The frame's segmented strip is
 *     drawn from the positive per-GL-account balances (BigInt-scaled to
 *     relative weights; shown only with two or more accounts).
 *   - «اسناد منتظر تأیید»: journals with status `submitted` from
 *     GET .../accounting/journal-entries. The frame's «قدیمی‌ترین N روز»
 *     line is omitted: the list carries only the document business date, not
 *     the submission time, so a wait duration cannot be computed honestly.
 *   - journals card: same list, newest business date first; the frame's
 *     filter buttons (وضعیت سند / دورهٔ مالی) have no behaviour in the frame
 *     and are not rendered — «همه اسناد» links to the journals tab.
 *   Beyond the frame, the two real aging charts are kept below the reference
 *   cards (ChartCard hides itself on 403). The older donut / open-balance
 *   bar / line-count tiles were dropped: the lifecycle card and the KPI row
 *   now carry the same facts. Known leftover: the aging charts still take
 *   `Number(openBalanceRial)` (AgingBucketsChart prop type); not changed here.
 *
 * Open-balance sums use BigInt (workingOpenBalanceRial); money is compacted by
 * OverviewCompact (BigInt) and falls back to the exact grouped number below
 * one million. Aging is fetched as-of `systemClock.today()` (business date).
 */
export function FinanceHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const { amountDisplayUnit } = useTenantSettings();
  const base = slug ? tenantBasePath(slug) : '';
  const today = systemClock.today();
  const overview = useApiGet<FinanceOverviewResponse>(
    slug ? `${base}/api/v1/finance/overview` : null,
    [slug],
  );
  const ar = useApiGet<OpenItemListResponse>(slug ? `${base}/api/v1/finance/ar/open-items` : null, [
    slug,
  ]);
  const ap = useApiGet<OpenItemListResponse>(slug ? `${base}/api/v1/finance/ap/open-items` : null, [
    slug,
  ]);
  const treasury = useApiGet<TreasuryAccountListResponse>(
    slug ? `${base}/api/v1/finance/treasury/accounts` : null,
    [slug],
  );
  const chart = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
  ]);
  const trialBalance = useApiGet<TrialBalanceResponse>(
    slug ? `${base}/api/v1/finance/reports/trial-balance?asOfDate=${today}` : null,
    [slug, today],
  );
  const journals = useApiGet<JournalListResponse>(
    slug ? `${base}/api/v1/accounting/journal-entries` : null,
    [slug],
  );
  const arAging = useApiGet<AgingResponse>(
    slug ? `${base}/api/v1/finance/ar/aging?asOfDate=${today}` : null,
    [slug, today],
  );
  const apAging = useApiGet<AgingResponse>(
    slug ? `${base}/api/v1/finance/ap/aging?asOfDate=${today}` : null,
    [slug, today],
  );

  if (!slug) return null;

  /** Compact tile amount (BigInt) — exact grouped digits below one million. */
  const amountText = (digits: string): string => {
    const parts = compactAmountParts(digits, amountDisplayUnit);
    if (!parts) {
      return formatChartAmount(digits, { unit: amountDisplayUnit, includeSuffix: true });
    }
    return `${parts.value} ${parts.scale} ${amountUnitLabel(amountDisplayUnit)}`;
  };
  /** KPI tile value + unit («۱٫۹۴» / «میلیارد ریال»); hover shows the exact amount. */
  const tileAmount = (digits: string): { value: ReactNode; scale: string } => {
    const parts = compactAmountParts(digits, amountDisplayUnit);
    const exact = formatRialFaWithUnit(digits, amountDisplayUnit);
    if (!parts) {
      return {
        value: <span title={exact}>{formatRialFa(digits, amountDisplayUnit)}</span>,
        scale: amountUnitLabel(amountDisplayUnit),
      };
    }
    return {
      value: <span title={exact}>{parts.value}</span>,
      scale: `${parts.scale} ${amountUnitLabel(amountDisplayUnit)}`,
    };
  };

  // AR overdue share — every aging bucket except `current`, summed as BigInt.
  let arOverdue: { readonly digits: string; readonly percent: number | null } | null = null;
  if (arAging.status === 'ready') {
    let overdue = 0n;
    let all = 0n;
    for (const bucket of arAging.data.buckets) {
      const amount = BigInt(bucket.openBalanceRial);
      all += amount;
      if (bucket.bucket !== 'current') overdue += amount;
    }
    arOverdue = { digits: overdue.toString(), percent: percentOfRial(overdue, all) };
  }

  // Treasury balance needs all three reads; 403 on any hides the tile.
  const treasuryDeps = [treasury, chart, trialBalance] as const;
  const treasuryForbidden = treasuryDeps.some(
    (d) => d.status === 'error' && d.error.status === 403,
  );
  const treasuryFailed = treasuryDeps.some((d) => d.status === 'error');
  const treasuryResult =
    treasury.status === 'ready' && chart.status === 'ready' && trialBalance.status === 'ready'
      ? treasuryBalance(treasury.data, chart.data, trialBalance.data)
      : null;
  const treasuryKinds =
    treasury.status === 'ready'
      ? {
          bank: treasury.data.items.filter((a) => a.kind === 'bank').length,
          cash: treasury.data.items.filter((a) => a.kind === 'cash').length,
        }
      : null;

  const journalItems = journals.status === 'ready' ? journals.data.items : [];
  const lifecycleCounts: JournalLifecycleCounts | null =
    journals.status === 'ready'
      ? {
          draft: journalItems.filter((j) => j.status === 'draft').length,
          submitted: journalItems.filter((j) => j.status === 'submitted').length,
          approved: journalItems.filter((j) => j.status === 'approved').length,
          posted: journalItems.filter((j) => j.status === 'posted').length,
        }
      : null;
  const provisionalCount = lifecycleCounts
    ? lifecycleCounts.draft + lifecycleCounts.submitted + lifecycleCounts.approved
    : 0;
  const recentJournals: readonly JournalRow[] = [...journalItems]
    .sort(byBusinessDateDesc)
    .slice(0, RECENT_JOURNAL_ROWS)
    .map((j) => ({ ...j, id: j.journalId }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HubYarCard moduleId="finance" />
      {overview.status === 'loading' ? <LoadingState /> : null}
      {overview.status === 'error' && overview.error.status !== 403 ? (
        <StatePanel
          status={overview.error.status}
          title="خلاصه مالی قابل نمایش نیست"
          onRetry={overview.reload}
        />
      ) : null}

      {overview.status === 'ready' && overview.data.chartOfAccountsConfigured === false ? (
        <NoticeBanner tone="warning" style={{ marginBlockEnd: 0 }}>
          کدینگ حساب‌ها هنوز تعیین نشده است و ثبت سند، دریافتنی/پرداختنی و خزانه تا آن زمان بسته
          است. <Link to={`${base}/finance/accounts`}>انتخاب کدینگ پیش‌فرض</Link>
        </NoticeBanner>
      ) : null}

      {overview.status === 'ready' ? (
        <>
          <HubKpiGrid accent="finance">
            {ar.status === 'ready' ? (
              (() => {
                const open = tileAmount(workingOpenBalanceRial(ar.data.items));
                return (
                  <HubKpi
                    hero
                    icon="finance"
                    label="حساب‌های دریافتنی"
                    value={open.value}
                    scale={open.scale}
                    foot={
                      arOverdue
                        ? `${amountText(arOverdue.digits)} سررسید گذشته`
                        : `${workingOpenItemCount(ar.data.items).toLocaleString('fa-IR')} مورد باز`
                    }
                    footEnd={
                      arOverdue?.percent != null ? (
                        <span className="sk-num">{arOverdue.percent.toLocaleString('fa-IR')}٪</span>
                      ) : undefined
                    }
                    {...(arOverdue?.percent != null
                      ? { progress: { percent: arOverdue.percent, tone: 'danger' as const } }
                      : {})}
                  />
                );
              })()
            ) : ar.status === 'error' && ar.error.status !== 403 ? (
              <HubKpi
                hero
                icon="finance"
                label="حساب‌های دریافتنی"
                value="—"
                foot={<RetryButton onClick={ar.reload} />}
              />
            ) : null}
            {ap.status === 'ready' ? (
              (() => {
                const open = tileAmount(workingOpenBalanceRial(ap.data.items));
                const due = countDueThisWeek(ap.data.items, today);
                return (
                  <HubKpi
                    label="حساب‌های پرداختنی"
                    value={open.value}
                    scale={open.scale}
                    foot={
                      due > 0
                        ? `${due.toLocaleString('fa-IR')} فاکتور سررسید این هفته`
                        : 'فاکتور سررسید این هفته‌ای نیست'
                    }
                    {...(due > 0 ? { ticks: { total: due, hot: due, tone: 'warn' } } : {})}
                  />
                );
              })()
            ) : ap.status === 'error' && ap.error.status !== 403 ? (
              <HubKpi
                label="حساب‌های پرداختنی"
                value="—"
                foot={<RetryButton onClick={ap.reload} />}
              />
            ) : null}
            {treasuryForbidden ? null : treasuryFailed ? (
              <HubKpi
                label="موجودی خزانه"
                value="—"
                foot={
                  treasury.status === 'error' ? (
                    <RetryButton onClick={treasury.reload} />
                  ) : chart.status === 'error' ? (
                    <RetryButton onClick={chart.reload} />
                  ) : (
                    <RetryButton onClick={trialBalance.reload} />
                  )
                }
              />
            ) : treasury.status === 'ready' &&
              chart.status === 'ready' &&
              trialBalance.status === 'ready' ? (
              (() => {
                const balance = treasuryResult ? tileAmount(treasuryResult.total) : null;
                return (
                  <HubKpi
                    label="موجودی خزانه"
                    value={balance ? balance.value : '—'}
                    {...(balance ? { scale: balance.scale } : {})}
                    {...(treasuryKinds
                      ? {
                          foot: `${treasuryKinds.bank.toLocaleString('fa-IR')} حساب بانکی · ${treasuryKinds.cash.toLocaleString('fa-IR')} صندوق`,
                        }
                      : {})}
                    {...(treasuryResult && treasuryResult.weights.length > 1
                      ? { weights: treasuryResult.weights }
                      : {})}
                  />
                );
              })()
            ) : null}
            {journals.status === 'ready' ? (
              <HubKpi
                icon="invoice"
                label="اسناد منتظر تأیید"
                value={(lifecycleCounts?.submitted ?? 0).toLocaleString('fa-IR')}
                foot={`از ${provisionalCount.toLocaleString('fa-IR')} سند موقت`}
              />
            ) : journals.status === 'error' && journals.error.status !== 403 ? (
              <HubKpi
                icon="invoice"
                label="اسناد منتظر تأیید"
                value="—"
                foot={<RetryButton onClick={journals.reload} />}
              />
            ) : null}
          </HubKpiGrid>

          {journals.status === 'error' && journals.error.status === 403 ? null : (
            <V2Card>
              <V2CardHeader
                title="اسناد حسابداری"
                icon="invoice"
                actions={
                  <Link to={`${base}/finance/journals`}>
                    <V2Button variant="secondary" size="sm" icon="chevronLeft">
                      همه اسناد
                    </V2Button>
                  </Link>
                }
              />
              {journals.status === 'loading' ? <LoadingState /> : null}
              {journals.status === 'error' ? (
                <StatePanel
                  status={journals.error.status}
                  title="فهرست اسناد قابل نمایش نیست"
                  onRetry={journals.reload}
                />
              ) : null}
              {journals.status === 'ready' ? (
                <V2Table
                  columns={journalColumns(base, amountDisplayUnit)}
                  rows={recentJournals}
                  zebra
                  emptyState={<V2EmptyState icon="info" title="سندی ثبت نشده" />}
                />
              ) : null}
            </V2Card>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'stretch' }}>
            <div
              style={{ flex: '1.4 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              <OverviewLifecycleCard counts={lifecycleCounts} />
            </div>
            <div
              style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              <OverviewQuickReports base={base} />
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 12,
            }}
          >
            {arAging.status === 'error' && arAging.error.status === 403 ? null : (
              <ChartCard
                title="سن بدهی دریافتنی"
                subtitle={
                  arAging.status === 'ready'
                    ? `بر مبنای ${displayJalaliDate(arAging.data.asOfDate)}`
                    : undefined
                }
                isLoading={arAging.status === 'loading'}
                error={arAging.status === 'error' ? arAging.error : undefined}
                status={arAging.status === 'error' ? arAging.error.status : undefined}
                onRetry={arAging.reload}
              >
                {arAging.status === 'ready' ? (
                  <AgingBucketsChart
                    ariaLabel="سن بدهی دریافتنی بر حسب بازه"
                    emptyMessage="مانده باز سررسیدشده‌ای نیست."
                    amountUnit={amountDisplayUnit}
                    buckets={arAging.data.buckets.map((b) => ({
                      bucket: b.bucket,
                      label: agingBucketLabel(b.bucket),
                      openBalanceRial: Number(b.openBalanceRial),
                    }))}
                  />
                ) : null}
              </ChartCard>
            )}

            {apAging.status === 'error' && apAging.error.status === 403 ? null : (
              <ChartCard
                title="سن بدهی پرداختنی"
                subtitle={
                  apAging.status === 'ready'
                    ? `بر مبنای ${displayJalaliDate(apAging.data.asOfDate)}`
                    : undefined
                }
                isLoading={apAging.status === 'loading'}
                error={apAging.status === 'error' ? apAging.error : undefined}
                status={apAging.status === 'error' ? apAging.error.status : undefined}
                onRetry={apAging.reload}
              >
                {apAging.status === 'ready' ? (
                  <AgingBucketsChart
                    ariaLabel="سن بدهی پرداختنی بر حسب بازه"
                    emptyMessage="مانده باز سررسیدشده‌ای نیست."
                    amountUnit={amountDisplayUnit}
                    buckets={apAging.data.buckets.map((b) => ({
                      bucket: b.bucket,
                      label: agingBucketLabel(b.bucket),
                      openBalanceRial: Number(b.openBalanceRial),
                    }))}
                  />
                ) : null}
              </ChartCard>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
